import { Button } from "@/components/ui/button";
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { File, Download, X } from 'lucide-react';
import { useAttachments } from "./AttachmentContext";
import { useState, useEffect } from "react";

const AttachmentDeleteButton = ({ attachment, className = "" }) => {
    const { setAttachmentToDelete, setDeleteModalOpen } = useAttachments();

    return (
        <Button
            type="button"
            size="icon"
            variant="ghost"
            className={className}
            onClick={(e) => {
                e.stopPropagation();
                setAttachmentToDelete(attachment);
                setDeleteModalOpen(true);
            }}
            tabIndex={-1}
        >
            <X className="h-3 w-3" />
        </Button>
    );
};

// Consolidated drag start handler
const createDragStartHandler = (attachment, getAttachmentUrl, isMinimal = false) => {
    return (e) => {
        const isImage = attachment.contentType && attachment.contentType.startsWith('image/');
        const imageUrl = isImage ? getAttachmentUrl(attachment) : null;
        
        e.dataTransfer.setData('text/plain', 'attachment');
        e.dataTransfer.setData('application/json', JSON.stringify({
            type: 'attachment',
            attachmentType: isImage ? 'image' : 'file',
            attachment: {
                key: attachment.key,
                url: isImage ? imageUrl : attachment.url,
                filename: attachment.filename || attachment.name,
                alt: attachment.filename || attachment.name,
                contentType: attachment.contentType,
                fileSize: attachment.size
            }
        }));
        e.dataTransfer.effectAllowed = 'copy';
        
        // Create drag preview
        const dragPreview = document.createElement('div');
        const iconSize = isMinimal ? '16px' : '24px';
        const padding = isMinimal ? '6px 10px' : '8px 12px';
        const fontSize = isMinimal ? '12px' : '14px';
        const borderRadius = isMinimal ? '6px' : '8px';
        
        const iconHtml = isImage && imageUrl 
            ? `<img src="${imageUrl}" style="width: ${iconSize}; height: ${iconSize}; object-fit: cover; border-radius: 4px;" />`
            : `<svg style="width: ${iconSize}; height: ${iconSize}; color: #9ca3af;" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                <polyline points="14,2 14,8 20,8"></polyline>
               </svg>`;
        
        dragPreview.innerHTML = `
            <div style="
                display: flex; 
                align-items: center; 
                gap: 8px; 
                padding: ${padding}; 
                background: white; 
                border: 1px solid #e5e7eb; 
                border-radius: ${borderRadius}; 
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                font-size: ${fontSize};
                color: #374151;
                pointer-events: none;
            ">
                ${iconHtml}
                <span>${attachment.filename || attachment.name}</span>
            </div>
        `;
        dragPreview.style.position = 'absolute';
        dragPreview.style.top = '-1000px';
        document.body.appendChild(dragPreview);
        e.dataTransfer.setDragImage(dragPreview, 0, 0);
        
        // Clean up after drag
        setTimeout(() => {
            document.body.removeChild(dragPreview);
        }, 0);
    };
};

const ImageAttachment = ({ attachment }) => {
    const { handleOpenAttachment, getAttachmentUrl } = useAttachments();
    const imageUrl = getAttachmentUrl(attachment);
    const [isLoaded, setIsLoaded] = useState(false);

    // Reset loaded state when image URL changes
    useEffect(() => {
        if (imageUrl) setIsLoaded(false);
    }, [imageUrl]);

    const isLoading = !imageUrl || !isLoaded;

    return (
        <div 
            className="relative w-32 h-28 rounded-lg overflow-hidden border bg-neutral-100 dark:bg-neutral-900 group cursor-pointer"
            onClick={() => handleOpenAttachment(attachment)}
            draggable
            onDragStart={createDragStartHandler(attachment, getAttachmentUrl)}
        >
            {/* Skeleton loader */}
            {isLoading && (
                <div className="absolute inset-0 bg-muted animate-pulse z-10" />
            )}

            {imageUrl && (
                <img
                    src={imageUrl}
                    alt={attachment.filename || attachment.name}
                    className={`w-full h-full object-cover transition-transform transition-opacity duration-500 group-hover:scale-105 ${
                        isLoading ? 'opacity-0' : 'opacity-100'
                    }`}
                    onLoad={() => setIsLoaded(true)}
                />
            )}
            
            {/* Overlays */}
            <div className="absolute inset-0 bg-black/10" />
            
            <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/50 to-transparent">
                <div className="text-xs text-white truncate">
                    {attachment.filename || attachment.name}
                </div>
            </div>
            <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                <AttachmentDeleteButton 
                    attachment={attachment} 
                    className="h-6 w-6 bg-black/50 hover:bg-black/70 text-white border-none"
                />
            </div>
        </div>
    );
};

const FileAttachment = ({ attachment }) => {
    const { handleOpenAttachment, getAttachmentUrl } = useAttachments();

    return (
        <div 
            className="relative w-32 h-28 rounded-lg border bg-neutral-100 dark:bg-neutral-900 flex flex-col items-center justify-center p-3 gap-2 group cursor-pointer hover:bg-neutral-200 dark:hover:bg-neutral-800 transition-all duration-200 hover:shadow-md"
            onClick={() => handleOpenAttachment(attachment)}
            draggable
            onDragStart={createDragStartHandler(attachment, getAttachmentUrl)}
        >
            <div className="flex flex-col items-center gap-1 transform group-hover:scale-105 transition-transform duration-200">
                <File className="h-8 w-8 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300" />
                <div className="text-xs text-center text-neutral-600 dark:text-neutral-400 line-clamp-2 w-full max-w-[90%]">
                    {attachment.filename || attachment.name}
                </div>
                <Download className="h-3.5 w-3.5 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300 mt-1" />
            </div>
            <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <AttachmentDeleteButton 
                    attachment={attachment} 
                    className="h-6 w-6"
                />
            </div>
        </div>
    );
};

const MinimalAttachmentItem = ({ attachment, index }) => {
    const { getAttachmentUrl, setAttachmentToDelete, setDeleteModalOpen } = useAttachments();
    const imageUrl = getAttachmentUrl(attachment);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        if (imageUrl) setIsLoaded(false);
    }, [imageUrl]);

    const isImage = attachment.contentType && attachment.contentType.startsWith('image/');
    const isLoading = isImage && (!imageUrl || !isLoaded);

    return (
        <li
            key={attachment.key || attachment.id || index}
            className="flex items-center gap-2 py-1 min-w-0 cursor-move"
            draggable
            onDragStart={createDragStartHandler(attachment, getAttachmentUrl, true)}
        >
            {/* Thumbnail or icon */}
            {isImage ? (
                <div className="relative w-4 h-4 rounded border flex-shrink-0 overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                    {isLoading && (
                        <div className="absolute inset-0 bg-muted animate-pulse z-10" />
                    )}
                    {imageUrl && (
                        <img
                            src={imageUrl}
                            alt={attachment.name || attachment.filename}
                            className={`w-full h-full object-cover transition-opacity duration-300 ${
                                isLoading ? 'opacity-0' : 'opacity-100'
                            }`}
                            onLoad={() => setIsLoaded(true)}
                        />
                    )}
                </div>
            ) : (
                <File className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            )}
            {/* File name */}
            <div className="flex-1 min-w-0">
                <TooltipProvider delayDuration={0}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="truncate text-xs text-muted-foreground block">
                                {attachment.name || attachment.filename}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>{attachment.name || attachment.filename}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
            {/* Remove button */}
            <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-4 w-4 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                    e.stopPropagation();
                    setAttachmentToDelete(attachment);
                    setDeleteModalOpen(true);
                }}
                tabIndex={-1}
            >
                ×
            </Button>
        </li>
    );
};

export default function AttachmentList({ style = "grid" }) {
    const { task } = useAttachments();
    
    if (!task?.attachments?.length) {
        return null;
    }

    if (style === "minimal") {
        return (
            <div className="space-y-1">
                {task.attachments.map((attachment, index) => (
                    <MinimalAttachmentItem 
                        key={attachment.key || attachment.id || index}
                        attachment={attachment} 
                        index={index} 
                    />
                ))}
            </div>
        );
    }

    // Grid style for existing tasks
    return (
        <div className="relative">
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-neutral-200 dark:scrollbar-thumb-neutral-800">
                {task.attachments.map((attachment, index) => (
                    <div key={index} className="flex-shrink-0 group relative">
                        {attachment.contentType?.startsWith('image/') ? (
                            <ImageAttachment attachment={attachment} />
                        ) : (
                            <FileAttachment attachment={attachment} />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
} 